/**
 * Personalplanung — HR Workforce Planning with FTE demand vs. availability.
 *
 * Features:
 * - KPI cards: next week FTE, available staff, delta, monthly costs
 * - Weekly overview table with status badges
 * - Stacked bar + line trend chart (Recharts)
 * - Collapsible staff availability grid
 * - Monthly cost preview card
 *
 * @page /personalplanung
 * @roles admin, manager
 * @api /api/personalplanung, /api/personalplanung/mitarbeiter, /api/personalplanung/kosten
 * @i18n personalplanung
 */
import { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import useTabParam from '../hooks/useTabParam';
import { useStandort } from '../hooks/useStandort';
import StandortSelect from '../components/StandortSelect';
import { PageHeader, KpiCard, Card } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import {
  Users, TrendingUp, TrendingDown, Euro, ChevronDown, ChevronUp,
  Check, Palmtree, Thermometer, CalendarOff, Calculator, RefreshCw, ArrowUpDown, Settings,
  Plus, X, Briefcase, UserCheck, Search as SearchIcon, Pencil, AlertTriangle, Info
} from 'lucide-react';
import ModulEinstellungen from '../components/ModulEinstellungen';
import MitarbeiterListItem from '../components/MitarbeiterListItem';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Line, ComposedChart, Cell
} from 'recharts';

const API = import.meta.env.VITE_API_URL || '';

function api(path) {
  return fetch(`${API}${path}`, { credentials: 'include' }).then(r => r.json());
}

// ─── Helpers ────────────────────────────────────────────────────
function fmtMin(v) {
  if (v == null) return '–';
  return Number(v).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' min';
}
function fmtNum(v) {
  if (v == null) return '–';
  return Number(v).toLocaleString('de-DE');
}

// ═══════════════════════════════════════════════════════════════
// MONATSPLANUNG TAB (v2.224.0)
// ═══════════════════════════════════════════════════════════════
// ─── Stellen Status Badge ───
const STELLEN_STATUS = {
  geplant: { label: 'Geplant', cls: 'bg-gray-500/10 text-gray-500' },
  ausgeschrieben: { label: 'Ausgeschrieben', cls: 'bg-blue-500/10 text-blue-600' },
  in_besetzung: { label: 'In Besetzung', cls: 'bg-amber-500/10 text-amber-600' },
  besetzt: { label: 'Besetzt', cls: 'bg-emerald-500/10 text-emerald-600' },
  storniert: { label: 'Storniert', cls: 'bg-red-500/10 text-red-500' },
};

// ─── LohnkostenModal ───
function LohnkostenModal({ open, onClose, onSave, ma, jahr }) {
  const { t, locale } = useLanguage();
  const [jahreslohnkosten, setJahreslohnkosten] = useState('');
  const [bereich, setBereich] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ma && open) {
      setJahreslohnkosten(ma.jahreslohnkosten || '');
      setBereich(ma.bereich || '');
    }
  }, [ma, open]);

  if (!open) return null;

  const proMonat = jahreslohnkosten ? (parseFloat(jahreslohnkosten) / 12) : null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/personalplanung/jahresplanung/${ma.mitarbeiter_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jahreslohnkosten: jahreslohnkosten ? parseFloat(jahreslohnkosten) : null, bereich: bereich || null, jahr })
      });
      onSave();
      onClose();
    } finally { setSaving(false); }
  };

  const BEREICHE = [
    { value: 'pick', label: t('pp.bereichPick') },
    { value: 'pack', label: t('pp.bereichPack') },
    { value: 'annahme', label: t('pp.bereichAnnahme') },
    { value: 'retouren', label: t('pp.bereichRetouren') },
    { value: 'verwaltung', label: t('pp.bereichVerwaltung') },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-card)] w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-[var(--text-primary)]">
            {t('pp.jahreslohnkostenEdit')} — {ma?.name}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-hover)]"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3 text-xs">
          <div>
            <label className="block text-[var(--text-tertiary)] mb-1">{t('pp.jahreslohnkosten')} (€)</label>
            <input type="number" step="0.01" value={jahreslohnkosten} onChange={e => setJahreslohnkosten(e.target.value)}
              className="w-full border border-[var(--border-card)] rounded-lg px-3 py-2 bg-[var(--bg-card)] text-[var(--text-primary)] text-sm font-mono"
              placeholder="42.000"
            />
          </div>
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-blue-500/5 border border-blue-500/15">
            <Info className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
            <span className="text-[10px] text-[var(--text-secondary)]">{t('pp.jahreslohnkostenHint')}</span>
          </div>
          {proMonat != null && (
            <div className="text-sm font-mono text-[var(--text-primary)] font-semibold text-center py-1">
              = €{proMonat.toLocaleString(locale === 'de' ? 'de-AT' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t('pp.proMonat')}
            </div>
          )}
          <div>
            <label className="block text-[var(--text-tertiary)] mb-1">{t('pp.bereich')}</label>
            <select value={bereich} onChange={e => setBereich(e.target.value)}
              className="w-full border border-[var(--border-card)] rounded-lg px-3 py-2 bg-[var(--bg-card)] text-[var(--text-primary)]">
              <option value="">–</option>
              {BEREICHE.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-xs rounded-lg border border-[var(--border-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]">
            {t('common.cancel')}
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 text-xs rounded-lg bg-[#338edc] text-white hover:bg-[#2a7bc4] disabled:opacity-50">
            {saving ? '...' : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── StelleModal ───
function StelleModal({ open, onClose, onSave, stelle, standorte }) {
  const { t, locale } = useLanguage();
  const [form, setForm] = useState({
    titel: '', lager_rolle: 'pick_pack', standort_id: '', stunden_woche: 40,
    jahreslohnkosten: '', bereich: '',
    geplant_ab_jahr: new Date().getFullYear(), geplant_ab_monat: new Date().getMonth() + 1,
    kommentar: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (stelle) {
      setForm({
        titel: stelle.titel || '',
        lager_rolle: stelle.lager_rolle || 'pick_pack',
        standort_id: stelle.standort_id || '',
        stunden_woche: stelle.stunden_woche || 40,
        jahreslohnkosten: stelle.jahreslohnkosten || '',
        bereich: stelle.bereich || '',
        geplant_ab_jahr: stelle.geplant_ab_jahr || new Date().getFullYear(),
        geplant_ab_monat: stelle.geplant_ab_monat || new Date().getMonth() + 1,
        kommentar: stelle.kommentar || ''
      });
    } else {
      setForm(f => ({ ...f, titel: '', jahreslohnkosten: '', kommentar: '', bereich: '' }));
    }
  }, [stelle, open]);

  if (!open) return null;

  const handleSave = async () => {
    if (!form.titel.trim()) return;
    setSaving(true);
    try {
      const url = stelle?.stelle_id
        ? `${API}/api/personalplanung/stellen/${stelle.stelle_id}`
        : `${API}/api/personalplanung/stellen`;
      const method = stelle?.stelle_id ? 'PUT' : 'POST';
      const body = {
        ...form,
        standort_id: form.standort_id || null,
        jahreslohnkosten: form.jahreslohnkosten ? parseFloat(form.jahreslohnkosten) : null,
      };
      await fetch(url, { method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      onSave();
      onClose();
    } finally { setSaving(false); }
  };

  const proMonat = form.jahreslohnkosten ? (parseFloat(form.jahreslohnkosten) / 12) : null;

  const monatsNamen = locale === 'de'
    ? ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
    : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const BEREICHE = [
    { value: 'pick', label: t('pp.bereichPick') },
    { value: 'pack', label: t('pp.bereichPack') },
    { value: 'annahme', label: t('pp.bereichAnnahme') },
    { value: 'retouren', label: t('pp.bereichRetouren') },
    { value: 'verwaltung', label: t('pp.bereichVerwaltung') },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-card)] w-full max-w-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-[var(--text-primary)]">
            {stelle?.stelle_id ? t('pp.stelleBearbeiten') : t('pp.stelleNeu')}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-hover)]"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="col-span-2">
            <label className="block text-[var(--text-tertiary)] mb-1">{t('pp.stelleTitel')} *</label>
            <input value={form.titel} onChange={e => setForm(f => ({ ...f, titel: e.target.value }))}
              className="w-full border border-[var(--border-card)] rounded-lg px-3 py-2 bg-[var(--bg-card)] text-[var(--text-primary)]"
              placeholder="z.B. Lagermitarbeiter/in"
            />
          </div>
          <div>
            <label className="block text-[var(--text-tertiary)] mb-1">{t('pp.bereich')}</label>
            <select value={form.bereich} onChange={e => setForm(f => ({ ...f, bereich: e.target.value }))}
              className="w-full border border-[var(--border-card)] rounded-lg px-3 py-2 bg-[var(--bg-card)] text-[var(--text-primary)]">
              <option value="">–</option>
              {BEREICHE.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[var(--text-tertiary)] mb-1">{t('pp.standort')}</label>
            <select value={form.standort_id} onChange={e => setForm(f => ({ ...f, standort_id: e.target.value }))}
              className="w-full border border-[var(--border-card)] rounded-lg px-3 py-2 bg-[var(--bg-card)] text-[var(--text-primary)]">
              <option value="">–</option>
              {(standorte || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[var(--text-tertiary)] mb-1">{t('pp.jahreslohnkosten')} (€)</label>
            <input type="number" value={form.jahreslohnkosten} onChange={e => setForm(f => ({ ...f, jahreslohnkosten: e.target.value }))}
              className="w-full border border-[var(--border-card)] rounded-lg px-3 py-2 bg-[var(--bg-card)] text-[var(--text-primary)]"
              placeholder="42.000"
            />
          </div>
          <div>
            <label className="block text-[var(--text-tertiary)] mb-1">h/W</label>
            <input type="number" value={form.stunden_woche} onChange={e => setForm(f => ({ ...f, stunden_woche: parseFloat(e.target.value) || 40 }))}
              className="w-full border border-[var(--border-card)] rounded-lg px-3 py-2 bg-[var(--bg-card)] text-[var(--text-primary)]"
            />
          </div>
          {proMonat != null && (
            <div className="col-span-2 text-center text-xs font-mono font-semibold text-[var(--text-primary)] py-1">
              = €{proMonat.toLocaleString(locale === 'de' ? 'de-AT' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t('pp.proMonat')}
            </div>
          )}
          <div className="col-span-2 flex items-start gap-2 px-3 py-2 rounded-lg bg-blue-500/5 border border-blue-500/15">
            <Info className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
            <span className="text-[10px] text-[var(--text-secondary)]">{t('pp.jahreslohnkostenHint')}</span>
          </div>
          <div>
            <label className="block text-[var(--text-tertiary)] mb-1">{t('pp.geplantAb')}</label>
            <div className="flex gap-2">
              <select value={form.geplant_ab_monat} onChange={e => setForm(f => ({ ...f, geplant_ab_monat: parseInt(e.target.value) }))}
                className="flex-1 border border-[var(--border-card)] rounded-lg px-2 py-2 bg-[var(--bg-card)] text-[var(--text-primary)]">
                {monatsNamen.map((mn, i) => <option key={i} value={i + 1}>{mn}</option>)}
              </select>
              <input type="number" value={form.geplant_ab_jahr} onChange={e => setForm(f => ({ ...f, geplant_ab_jahr: parseInt(e.target.value) }))}
                className="w-20 border border-[var(--border-card)] rounded-lg px-2 py-2 bg-[var(--bg-card)] text-[var(--text-primary)]"
              />
            </div>
          </div>
          <div>
            <label className="block text-[var(--text-tertiary)] mb-1">{t('pp.kommentar')}</label>
            <input value={form.kommentar} onChange={e => setForm(f => ({ ...f, kommentar: e.target.value }))}
              className="w-full border border-[var(--border-card)] rounded-lg px-3 py-2 bg-[var(--bg-card)] text-[var(--text-primary)]"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-xs rounded-lg border border-[var(--border-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]">
            {t('common.cancel')}
          </button>
          <button onClick={handleSave} disabled={saving || !form.titel.trim()}
            className="px-4 py-2 text-xs rounded-lg bg-[#338edc] text-white hover:bg-[#2a7bc4] disabled:opacity-50">
            {saving ? '...' : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Bereich label + group helpers ───
const BEREICH_LABELS = { pick: 'pp.bereichPick', pack: 'pp.bereichPack', annahme: 'pp.bereichAnnahme', retouren: 'pp.bereichRetouren', verwaltung: 'pp.bereichVerwaltung' };
const GRUPPEN = [
  { key: 'outbound', labelKey: 'pp.gruppeOutbound', sumKey: 'pp.summeOutbound', bereiche: ['pick', 'pack'] },
  { key: 'inbound', labelKey: 'pp.gruppeInbound', sumKey: 'pp.summeInbound', bereiche: ['annahme', 'retouren'] },
  { key: 'verwaltung', labelKey: 'pp.gruppeVerwaltung', sumKey: 'pp.summeVerwaltung', bereiche: ['verwaltung'] },
];

function MonatsplanungTab() {
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.rolle === 'administrator';
  const [jahr, setJahr] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showStelleModal, setShowStelleModal] = useState(false);
  const [editStelle, setEditStelle] = useState(null);
  const [showLohnModal, setShowLohnModal] = useState(false);
  const [editMa, setEditMa] = useState(null);
  const [standorte, setStandorte] = useState([]);
  // Inline override editing: { maId, monat } or null
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');

  const monatsNamen = locale === 'de'
    ? ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
    : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const load = useCallback(() => {
    setLoading(true);
    api(`/api/personalplanung/monatsplan?jahr=${jahr}`)
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [jahr]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api('/api/standorte').then(d => setStandorte(d.standorte || d || [])).catch(() => {}); }, []);

  const generieren = async () => {
    setGenerating(true);
    try {
      await fetch(`${API}/api/personalplanung/monatsplan/generieren`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jahr })
      });
      load();
    } finally { setGenerating(false); }
  };

  // ─── Override save/reset ───
  const saveOverride = async (maId, monat, value) => {
    const numVal = parseFloat(value);
    if (isNaN(numVal) || numVal < 0) { setEditingCell(null); return; }
    try {
      await fetch(`${API}/api/personalplanung/monatsplan/${maId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jahr, monat, override_kosten: Math.round(numVal) })
      });
      load();
    } catch { /* ignore */ }
    setEditingCell(null);
  };

  const resetOverride = async (maId, monat) => {
    try {
      await fetch(`${API}/api/personalplanung/monatsplan/${maId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jahr, monat, override_kosten: null })
      });
      load();
    } catch { /* ignore */ }
  };

  const fmtEur = (v) => {
    if (v == null || v === 0) return '–';
    return `€${Math.round(v).toLocaleString(locale === 'de' ? 'de-AT' : 'en-US')}`;
  };

  // Chart data from monatsSummen
  const chartData = useMemo(() => {
    if (!data?.monatsSummen) return [];
    return Array.from({ length: 12 }, (_, i) => {
      const m = i + 1;
      const s = data.monatsSummen[m];
      return {
        name: monatsNamen[i],
        kosten: s?.dg_kosten || 0,
        stellen: s?.stellen_kosten || 0,
      };
    });
  }, [data, monatsNamen]);

  // Group rows by bereich
  const grouped = useMemo(() => {
    if (!data?.rows) return {};
    const map = {};
    for (const r of data.rows) {
      const b = r.bereich || 'pick';
      if (!map[b]) map[b] = [];
      map[b].push(r);
    }
    return map;
  }, [data]);

  if (loading && !data) return <div className="text-xs text-[var(--text-tertiary)]">{t('common.loading')}</div>;

  // ─── Render helper: MA row ───
  const renderMaRow = (ma) => {
    const jahresGesamt = Object.values(ma.monate).reduce((s, m) => s + (m.dg_kosten || 0), 0);
    return (
      <tr key={ma.mitarbeiter_id} className="border-b border-[var(--border-card)]/50 hover:bg-[var(--bg-hover)]/50">
        <td className="px-2 py-1.5 font-medium text-[var(--text-primary)] sticky left-0 bg-[var(--bg-card)] z-10 whitespace-nowrap">
          <div className="flex items-center gap-1.5">
            <button onClick={() => { setEditMa(ma); setShowLohnModal(true); }}
              className="p-0.5 rounded hover:bg-[var(--bg-hover)] text-[var(--text-muted)] hover:text-[#338edc]" title={t('pp.jahreslohnkostenEdit')}>
              <Pencil className="w-3 h-3" />
            </button>
            <MitarbeiterListItem
              mitarbeiter={{ ...ma, id: ma.mitarbeiter_id }}
              size="sm"
              onClick={() => navigate(`/mitarbeiter/${ma.mitarbeiter_id}`)}
              trailing={ma.kosten_geschaetzt ? (
                <span title={t('pp.kostenGeschaetzt')}>
                  <AlertTriangle className="w-3 h-3 text-amber-500" />
                </span>
              ) : null}
            />
          </div>
        </td>
        {Array.from({ length: 12 }, (_, i) => {
          const monat = i + 1;
          const m = ma.monate[monat];
          const isEditing = editingCell?.maId === ma.mitarbeiter_id && editingCell?.monat === monat;
          const hasOverride = m?.override_kosten != null;

          if (isEditing) {
            return (
              <td key={i} className="px-1 py-0.5">
                <input
                  type="number"
                  autoFocus
                  className="w-full text-right text-[11px] font-mono px-1 py-0.5 border border-[#338edc] rounded bg-[var(--bg-card)] text-[var(--text-primary)] outline-none"
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') saveOverride(ma.mitarbeiter_id, monat, editValue);
                    if (e.key === 'Escape') setEditingCell(null);
                  }}
                  onBlur={() => saveOverride(ma.mitarbeiter_id, monat, editValue)}
                />
              </td>
            );
          }

          return (
            <td key={i}
              className={`px-2 py-1.5 text-right font-mono cursor-pointer group relative ${
                hasOverride ? 'text-[#338edc] font-semibold' :
                m?.kosten_geschaetzt ? 'text-[var(--text-muted)] italic' : 'text-[var(--text-secondary)]'
              } hover:bg-[var(--bg-hover)]/50`}
              title={hasOverride ? `${t('pp.override')}: €${Math.round(m.override_kosten).toLocaleString()} — ${t('pp.overrideReset')}` : t('pp.overrideClick')}
              onClick={() => { setEditingCell({ maId: ma.mitarbeiter_id, monat }); setEditValue(String(m?.dg_kosten || '')); }}
            >
              <span>{m?.dg_kosten ? fmtEur(m.dg_kosten) : '–'}</span>
              {hasOverride && (
                <button
                  className="absolute -top-1 -right-0.5 hidden group-hover:block p-0.5 rounded-full bg-[var(--bg-card)] border border-[var(--border-card)] shadow-sm"
                  title={t('pp.overrideReset')}
                  onClick={e => { e.stopPropagation(); resetOverride(ma.mitarbeiter_id, monat); }}
                >
                  <X className="w-2.5 h-2.5 text-[var(--text-muted)]" />
                </button>
              )}
            </td>
          );
        })}
        <td className="px-2 py-1.5 text-right font-mono font-bold text-[var(--text-primary)]">
          {jahresGesamt > 0 ? fmtEur(jahresGesamt) : '–'}
        </td>
      </tr>
    );
  };

  // ─── Render helper: Gruppe summary row ───
  const renderGruppeSumme = (gruppeKey, label) => {
    if (!data?.gruppeSummen?.[gruppeKey]) return null;
    const gs = data.gruppeSummen[gruppeKey];
    const jahresGesamt = Object.values(gs).reduce((s, m) => s + (m.dg_kosten || 0), 0);
    const avgFte = Object.values(gs).reduce((s, m) => s + (m.fte || 0), 0) / 12;
    return (
      <tr className="border-t border-[var(--border-card)] font-semibold text-[var(--text-primary)]">
        <td className="px-2 py-1.5 sticky left-0 bg-[var(--bg-card)] z-10 text-[10px]">
          {label} <span className="font-normal text-[var(--text-tertiary)]">({Math.round(avgFte * 10) / 10} FTE)</span>
        </td>
        {Array.from({ length: 12 }, (_, i) => (
          <td key={i} className="px-2 py-1.5 text-right font-mono text-[11px]">
            <div>{gs[i + 1]?.dg_kosten ? fmtEur(gs[i + 1].dg_kosten) : '–'}</div>
            <div className="text-[9px] font-normal text-[var(--text-muted)]">{gs[i + 1]?.fte || 0} FTE</div>
          </td>
        ))}
        <td className="px-2 py-1.5 text-right font-mono text-[var(--text-primary)]">{fmtEur(jahresGesamt)}</td>
      </tr>
    );
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center gap-3">
        <button onClick={() => setJahr(j => j - 1)} className="p-1.5 rounded-lg hover:bg-[var(--bg-hover)] text-[var(--text-tertiary)]">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <span className="text-sm font-bold min-w-[60px] text-center text-[var(--text-primary)]">{jahr}</span>
        <button onClick={() => setJahr(j => j + 1)} className="p-1.5 rounded-lg hover:bg-[var(--bg-hover)] text-[var(--text-tertiary)]">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => { setEditStelle(null); setShowStelleModal(true); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--border-card)] rounded-lg hover:bg-[var(--bg-hover)] text-[var(--text-secondary)]">
            <Plus className="w-3.5 h-3.5" />{t('pp.stelleNeu')}
          </button>
          <button onClick={generieren} disabled={generating}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#338edc] text-white rounded-lg hover:bg-[#2a7bc4] disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${generating ? 'animate-spin' : ''}`} />{t('pp.generieren')}
          </button>
          {isAdmin && (
            <button onClick={() => setResetConfirm(true)} disabled={resetting}
              title={t('pp.autoStammdatenHint', 'Löscht alle Overrides für das Jahr und berechnet Monats-Werte neu aus den Lohnkosten-Phasen. Nicht umkehrbar.')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 disabled:opacity-50">
              <RefreshCw className={`w-3.5 h-3.5 ${resetting ? 'animate-spin' : ''}`} />{t('pp.autoStammdaten', 'Automatisch aus Stammdaten')}
            </button>
          )}
        </div>
      </div>

      {/* #899 — Warning wenn MAs ohne Lohnkosten-Quelle */}
      {data?.summary && (data.summary.ma_gesamt - data.summary.ma_mit_planung) > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-card p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">
              {t('pp.fehltWarning', '{n} Mitarbeiter ohne gepflegte Lohnkosten').replace('{n}', data.summary.ma_gesamt - data.summary.ma_mit_planung)}
            </div>
            <div className="text-xs text-amber-800 mt-1">
              {t('pp.fehltHint', 'Ohne Jahres-Lohnkosten können keine Monats-Kosten berechnet werden. Bitte HR-Audit öffnen und Daten pflegen.')}
            </div>
          </div>
          <button onClick={() => navigate('/hr-audit?check=lohnkosten_unvollstaendig')}
            className="px-3 py-1.5 text-xs font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-700">
            {t('pp.jetztAnlegen', 'Jetzt anlegen')}
          </button>
        </div>
      )}

      {/* Reset-Confirm Dialog */}
      {resetConfirm && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={() => !resetting && setResetConfirm(false)}>
          <div className="bg-surface-card border border-line rounded-card max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-content mb-2">{t('pp.autoResetTitle', 'Alle Overrides zurücksetzen?')}</h3>
            <p className="text-xs text-[var(--text-secondary)] mb-4">
              {t('pp.autoResetMsg', 'Alle manuellen Monats-Overrides für {jahr} werden gelöscht. Neue Monats-Werte werden pro-rata aus den aktiven Lohnkosten-Phasen berechnet. Diese Aktion ist nicht umkehrbar.').replace('{jahr}', jahr)}
            </p>
            <div className="flex items-center justify-end gap-2">
              <button disabled={resetting} onClick={() => setResetConfirm(false)}
                className="px-3 py-1.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-50">
                {t('common.cancel', 'Abbrechen')}
              </button>
              <button disabled={resetting} onClick={async () => {
                setResetting(true);
                try {
                  const r = await fetch(`${API}/api/admin/personalplanung/reset-overrides`, {
                    method: 'POST', credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ jahr })
                  });
                  const j = await r.json();
                  if (j.success) { setResetConfirm(false); load(); }
                  else alert(j.error || 'Fehler');
                } catch (e) { alert(String(e)); }
                finally { setResetting(false); }
              }}
                className="px-4 py-1.5 text-sm font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50">
                {resetting ? '…' : t('pp.autoResetConfirm', 'Zurücksetzen')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <StelleModal open={showStelleModal} onClose={() => { setShowStelleModal(false); setEditStelle(null); }} onSave={load} stelle={editStelle} standorte={standorte} />
      <LohnkostenModal open={showLohnModal} onClose={() => { setShowLohnModal(false); setEditMa(null); }} onSave={load} ma={editMa} jahr={jahr} />

      {/* Summary KPIs — with FTE per Bereich */}
      {data?.summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          <KpiCard label={t('pp.maGesamt')} value={data.summary.ma_gesamt} />
          <KpiCard label={t('pp.mitPlanung')} value={data.summary.ma_mit_planung} />
          <KpiCard label={t('pp.jahreskosten')} value={fmtEur(data.summary.jahres_dg_kosten)} />
          <KpiCard label={t('pp.offeneStellen')} value={data.summary.stellen_offen || 0} />
          <KpiCard label={t('pp.fteOutbound')} value={data.summary.fte?.outbound || 0} />
          <KpiCard label={t('pp.fteInbound')} value={data.summary.fte?.inbound || 0} />
          <KpiCard label={t('pp.fteVerwaltung')} value={data.summary.fte?.verwaltung || 0} />
          <KpiCard label={t('pp.fteGesamt')} value={data.summary.fte?.gesamt || 0} />
        </div>
      )}

      {/* Cost Chart */}
      {chartData.length > 0 && (
        <Card title={t('pp.kostenVerlauf')}>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-card, #E5E7EB)" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} tickFormatter={v => `€${(v/1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 11 }}
                  formatter={(v, name) => [fmtEur(v), name === 'kosten' ? t('pp.dgKosten') : t('pp.stellenKosten')]} />
                <Bar dataKey="kosten" name={t('pp.dgKosten')} stackId="cost" fill="#338edc" radius={[0, 0, 0, 0]} />
                <Bar dataKey="stellen" name={t('pp.stellenKosten')} stackId="cost" fill="#22D3EE" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Grouped Grid Table */}
      {data?.rows && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b border-[var(--border-card)]">
                  <th className="text-left px-2 py-2 font-semibold text-[var(--text-tertiary)] sticky left-0 bg-[var(--bg-card)] z-10 min-w-[180px]">
                    {t('common.employee')}
                  </th>
                  {monatsNamen.map((mn, i) => (
                    <th key={i} className="text-right px-2 py-2 font-semibold text-[var(--text-tertiary)] min-w-[70px]">{mn}</th>
                  ))}
                  <th className="text-right px-2 py-2 font-semibold text-[var(--text-tertiary)] min-w-[80px]">{t('pp.jahresGesamt')}</th>
                </tr>
              </thead>
              <tbody>
                {GRUPPEN.map(gruppe => {
                  const gruppeRows = gruppe.bereiche.flatMap(b => grouped[b] || []);
                  if (gruppeRows.length === 0 && !data.stellen?.some(st => gruppe.bereiche.includes(st.bereich))) return null;
                  return (
                    <Fragment key={gruppe.key}>
                      {/* Gruppe Header */}
                      <tr>
                        <td colSpan={14} className="px-2 py-2 text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-wider bg-[var(--bg-inset,#F8F9FC)] sticky left-0 z-10 border-t-2 border-[var(--border-card)]">
                          {t(gruppe.labelKey)}
                        </td>
                      </tr>
                      {/* Sub-groups */}
                      {gruppe.bereiche.map(b => {
                        const bRows = grouped[b] || [];
                        if (bRows.length === 0) return null;
                        return (
                          <Fragment key={b}>
                            {gruppe.bereiche.length > 1 && (
                              <tr>
                                <td colSpan={14} className="px-2 py-1 pl-4 text-[9px] font-semibold text-[var(--text-muted)] uppercase tracking-wider sticky left-0 bg-[var(--bg-card)] z-10">
                                  {t(BEREICH_LABELS[b])}
                                </td>
                              </tr>
                            )}
                            {bRows.map(renderMaRow)}
                          </Fragment>
                        );
                      })}
                      {/* Gruppe Summe */}
                      {renderGruppeSumme(gruppe.key, t(gruppe.sumKey))}
                    </Fragment>
                  );
                })}

                {/* ─── Geplante Stellen ─── */}
                {data.stellen?.length > 0 && (
                  <>
                    <tr className="border-t-2 border-dashed border-[var(--border-card)]">
                      <td colSpan={14} className="px-2 py-1.5 text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wider sticky left-0 bg-[var(--bg-card)] z-10">
                        <div className="flex items-center gap-1.5"><Briefcase className="w-3 h-3" />{t('pp.geplanteStellen')} ({data.stellen.length})</div>
                      </td>
                    </tr>
                    {data.stellen.map(st => {
                      const jahresGesamt = Object.values(st.monate).reduce((s, m) => s + (m.dg_kosten || 0), 0);
                      const statusInfo = STELLEN_STATUS[st.status] || STELLEN_STATUS.geplant;
                      return (
                        <tr key={`stelle-${st.stelle_id}`} className="border-b border-dashed border-[var(--border-card)]/50 hover:bg-[var(--bg-hover)]/30 bg-[var(--bg-inset,#F8F9FC)]/30">
                          <td className="px-2 py-1.5 sticky left-0 bg-[var(--bg-card)] z-10 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <Briefcase className="w-3 h-3 text-[var(--text-muted)]" />
                              <div>
                                <button onClick={() => { setEditStelle(st); setShowStelleModal(true); }}
                                  className="font-medium text-[var(--text-primary)] hover:text-[#338edc] cursor-pointer">{st.titel}</button>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <span className={`inline-block px-1.5 py-0 rounded text-[8px] font-bold ${statusInfo.cls}`}>{statusInfo.label}</span>
                                  <span className="text-[9px] text-[var(--text-muted)]">{st.stunden_woche}h/W · ab {st.geplant_ab}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                          {Array.from({ length: 12 }, (_, i) => {
                            const m = st.monate[i + 1];
                            return (
                              <td key={i} className={`px-2 py-1.5 text-right font-mono italic ${m?.aktiv ? 'text-[#22D3EE]' : 'text-[var(--text-muted)]'}`}>
                                {m?.dg_kosten ? fmtEur(m.dg_kosten) : '–'}
                              </td>
                            );
                          })}
                          <td className="px-2 py-1.5 text-right font-mono font-bold text-[#22D3EE] italic">
                            {jahresGesamt > 0 ? fmtEur(jahresGesamt) : '–'}
                          </td>
                        </tr>
                      );
                    })}
                  </>
                )}
              </tbody>
              {/* Footer: Gesamt */}
              <tfoot>
                <tr className="border-t-2 border-[var(--border-card)] font-bold bg-[var(--bg-inset,#F8F9FC)]">
                  <td className="px-2 py-2 text-[var(--text-primary)] sticky left-0 bg-[var(--bg-inset,#F8F9FC)] z-10">
                    {t('pp.summe')} <span className="font-normal text-[var(--text-tertiary)]">({data.summary?.fte?.gesamt || 0} FTE)</span>
                  </td>
                  {monatsNamen.map((_, i) => {
                    const s = data.monatsSummen?.[i + 1];
                    return (
                      <td key={i} className="px-2 py-2 text-right font-mono text-[var(--text-primary)]">
                        <div>{s?.dg_kosten ? fmtEur(s.dg_kosten) : '–'}</div>
                        <div className="text-[9px] font-normal text-[var(--text-muted)]">{s?.fte || 0} FTE</div>
                      </td>
                    );
                  })}
                  <td className="px-2 py-2 text-right font-mono text-[var(--text-primary)]">{fmtEur(data.summary?.jahres_dg_kosten)}</td>
                </tr>
                <tr className="text-[var(--text-tertiary)]">
                  <td className="px-2 py-1 sticky left-0 bg-[var(--bg-card)] z-10">{t('pp.arbeitstage')}</td>
                  {monatsNamen.map((_, i) => (
                    <td key={i} className="px-2 py-1 text-right font-mono">{data.monatsSummen?.[i + 1]?.arbeitstage || '–'}</td>
                  ))}
                  <td className="px-2 py-1 text-right font-mono">
                    {Object.values(data.monatsSummen || {}).reduce((s, m) => s + (m.arbeitstage || 0), 0)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// STUNDENSATZ TAB (v2.224.0)
// ═══════════════════════════════════════════════════════════════
function StundensatzTab() {
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const now = new Date();
  const [jahr, setJahr] = useState(now.getFullYear());
  const [monat, setMonat] = useState(now.getMonth() + 1);
  const [sollData, setSollData] = useState(null);
  const [vergleichData, setVergleichData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('soll'); // 'soll' | 'vergleich'

  const monatsNamen = Array.from({ length: 12 }, (_, i) =>
    new Date(2026, i, 1).toLocaleDateString(locale === 'de' ? 'de-AT' : 'en-US', { month: 'long' })
  );

  const loadSoll = useCallback(() => {
    setLoading(true);
    api(`/api/personalplanung/stundensatz?jahr=${jahr}`)
      .then(d => setSollData(d))
      .catch(() => setSollData(null))
      .finally(() => setLoading(false));
  }, [jahr]);

  const loadVergleich = useCallback(() => {
    setLoading(true);
    api(`/api/personalplanung/stundensatz/vergleich?jahr=${jahr}&monat=${monat}`)
      .then(d => setVergleichData(d))
      .catch(() => setVergleichData(null))
      .finally(() => setLoading(false));
  }, [jahr, monat]);

  useEffect(() => {
    if (view === 'soll') loadSoll();
    else loadVergleich();
  }, [view, loadSoll, loadVergleich]);

  const fmtEur = (v) => {
    if (v == null) return '–';
    return `€${typeof v === 'number' ? v.toLocaleString(locale === 'de' ? 'de-AT' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v}`;
  };
  const fmtEurRound = (v) => {
    if (v == null || v === 0) return '–';
    return `€${Math.round(v).toLocaleString(locale === 'de' ? 'de-AT' : 'en-US')}`;
  };

  if (loading && !sollData && !vergleichData) return <div className="text-xs text-[var(--text-tertiary)]">{t('common.loading')}</div>;

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center gap-3">
        <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] gap-[2px] border border-[var(--border-card)]">
          {[
            { key: 'soll', label: t('pp.sollBerechnung') },
            { key: 'vergleich', label: t('pp.sollVsIst') },
          ].map(v => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              className={`px-4 py-1.5 text-xs font-medium rounded-[8px] transition-all ${
                view === v.key
                  ? 'bg-white text-[var(--text-primary)] shadow-sm font-semibold'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <button onClick={() => setJahr(j => j - 1)} className="p-1 rounded hover:bg-[var(--bg-hover)] text-[var(--text-tertiary)]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <span className="text-sm font-bold text-[var(--text-primary)]">{jahr}</span>
          <button onClick={() => setJahr(j => j + 1)} className="p-1 rounded hover:bg-[var(--bg-hover)] text-[var(--text-tertiary)]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
          </button>
          {view === 'vergleich' && (
            <select
              value={monat}
              onChange={e => setMonat(parseInt(e.target.value))}
              className="ml-2 text-xs border border-[var(--border-card)] rounded-lg px-2 py-1.5 bg-[var(--bg-card)] text-[var(--text-primary)]"
            >
              {monatsNamen.map((mn, i) => <option key={i} value={i + 1}>{mn}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* ─── SOLL View ─── */}
      {view === 'soll' && sollData && (
        <>
          {/* Config info bar */}
          {sollData.config && (
            <div className="flex flex-wrap items-center gap-3 text-[10px] text-[var(--text-tertiary)]">
              <span>{t('pp.kalkAnwesenheitsstunden')}: <b className="text-[var(--text-secondary)]">{sollData.config.kalk_anwesenheitsstunden}h</b></span>
              <span>{t('pp.produktivitaet')}: <b className="text-[var(--text-secondary)]">{sollData.config.produktivitaet_pct}%</b></span>
              <span>{t('pp.formelLabel', 'Formel')}: <span className="font-mono text-[var(--text-secondary)]">€/h = {t('pp.jahreslohnkosten')} / ({t('pp.kalkStunden')} × h/W÷40) / ({t('pp.produktivitaet')}/100)</span></span>
            </div>
          )}

          {/* Summary KPIs */}
          {sollData.summary && (
            <div className="grid grid-cols-4 gap-3">
              <KpiCard label={t('pp.maGesamt')} value={sollData.summary.ma_gesamt} />
              <KpiCard label={t('pp.mitPlanung')} value={sollData.summary.ma_mit_planung} />
              <KpiCard label={`Ø ${t('pp.sollStundensatz')}`} value={sollData.summary.avg_soll_stundensatz ? `€${sollData.summary.avg_soll_stundensatz}` : '–'} />
              <KpiCard label={`${t('pp.dgKosten')} / ${t('pp.monatLabel')}`} value={fmtEurRound(sollData.summary.dg_monatskosten_gesamt)} />
            </div>
          )}

          {/* Table */}
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="border-b border-[var(--border-card)]">
                    {[t('common.employee'), 'h/W', t('pp.jahreslohnkosten'), t('pp.kalkStunden'), t('pp.produktivitaetCol'), t('pp.sollStundensatz')].map((h, i) => (
                      <th key={i} className={`px-2 py-2 font-semibold text-[var(--text-tertiary)] ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sollData.rows.map(r => (
                    <tr key={r.mitarbeiter_id} className="border-b border-[var(--border-card)]/50 hover:bg-[var(--bg-hover)]/50">
                      <td className="px-2 py-1.5 font-medium text-[var(--text-primary)] whitespace-nowrap">
                        <MitarbeiterListItem
                          mitarbeiter={{ ...r, id: r.mitarbeiter_id }}
                          size="sm"
                          onClick={() => navigate(`/mitarbeiter/${r.mitarbeiter_id}`)}
                          trailing={!r.hat_jahreslohnkosten ? (
                            <span title={t('pp.nichtHinterlegt')}>
                              <AlertTriangle className="w-3 h-3 text-amber-500" />
                            </span>
                          ) : null}
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono text-[var(--text-secondary)]">{r.wochenstunden_soll}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-[var(--text-secondary)]">{r.jahreslohnkosten ? fmtEurRound(r.jahreslohnkosten) : '–'}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-[var(--text-secondary)]">{r.kalk_stunden || '–'}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-[var(--text-secondary)]">{r.produktivitaet_pct ? `${r.produktivitaet_pct}%` : '–'}</td>
                      <td className="px-2 py-1.5 text-right font-mono font-bold text-[var(--text-primary)]">
                        {r.soll_stundensatz ? fmtEur(r.soll_stundensatz) : '–'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* ─── Vergleich View ─── */}
      {view === 'vergleich' && vergleichData && (
        <>
          {vergleichData.summary && (
            <div className="grid grid-cols-4 gap-3">
              <KpiCard label={t('pp.maGesamt')} value={vergleichData.summary.ma_gesamt} />
              <KpiCard label={t('pp.mitVergleich')} value={vergleichData.summary.ma_mit_vergleich} />
              <KpiCard label={`Ø SOLL €/h`} value={vergleichData.summary.avg_soll ? `€${vergleichData.summary.avg_soll}` : '–'} />
              <KpiCard label={`Ø IST €/h`} value={vergleichData.summary.avg_ist ? `€${vergleichData.summary.avg_ist}` : '–'} />
            </div>
          )}

          {vergleichData.summary?.ma_mit_vergleich === 0 ? (
            <Card>
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Info className="w-8 h-8 text-[var(--text-muted)] mb-3" />
                <p className="text-sm font-medium text-[var(--text-secondary)]">{t('pp.keineIstDaten')}</p>
                <p className="text-xs text-[var(--text-muted)] mt-1 max-w-md">{t('pp.keineIstDatenHint')}</p>
              </div>
            </Card>
          ) : (
            <Card title={`${t('pp.sollVsIst')} — ${monatsNamen[monat - 1]} ${jahr}`}>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-[var(--border-card)]">
                      {[t('common.employee'), 'SOLL €/h', `IST ${t('pp.dgKosten')}`, `IST ${t('pp.stunden')}`, 'IST €/h', t('pp.differenz'), '%'].map((h, i) => (
                        <th key={i} className={`px-2 py-2 font-semibold text-[var(--text-tertiary)] ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {vergleichData.rows.map(r => (
                      <tr key={r.mitarbeiter_id} className="border-b border-[var(--border-card)]/50 hover:bg-[var(--bg-hover)]/50">
                        <td className="px-2 py-1.5 font-medium text-[var(--text-primary)] whitespace-nowrap">
                          <MitarbeiterListItem
                            mitarbeiter={{ ...r, id: r.mitarbeiter_id }}
                            size="sm"
                            onClick={() => navigate(`/mitarbeiter/${r.mitarbeiter_id}`)}
                          />
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono text-[var(--text-secondary)]">{r.soll_stundensatz ? fmtEur(r.soll_stundensatz) : '–'}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-[var(--text-secondary)]">{r.ist_dg_kosten ? fmtEurRound(r.ist_dg_kosten) : '–'}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-[var(--text-secondary)]">{r.ist_stunden || '–'}</td>
                        <td className="px-2 py-1.5 text-right font-mono font-bold text-[var(--text-primary)]">{r.ist_stundensatz ? fmtEur(r.ist_stundensatz) : '–'}</td>
                        <td className={`px-2 py-1.5 text-right font-mono font-semibold ${r.diff > 0 ? 'text-red-600' : r.diff < 0 ? 'text-emerald-600' : 'text-[var(--text-secondary)]'}`}>
                          {r.diff != null ? `${r.diff > 0 ? '+' : ''}${fmtEur(r.diff)}` : '–'}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          {r.diff_pct != null ? (
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${
                              r.diff_pct > 5 ? 'bg-red-500/10 text-red-600' :
                              r.diff_pct < -5 ? 'bg-emerald-500/10 text-emerald-600' :
                              'bg-[var(--bg-inset)] text-[var(--text-secondary)]'
                            }`}>
                              {r.diff_pct > 0 ? '+' : ''}{r.diff_pct}%
                            </span>
                          ) : '–'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// FTE-PROGNOSE TAB (moved from Zeitvorgaben.jsx)
// ═══════════════════════════════════════════════════════════════
function FtePrognoseTab() {
  const { t } = useLanguage();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { standorte, standortId, setStandortId } = useStandort();

  const monthNames = Array.from({length: 12}, (_, i) => t('common.months.' + i));

  const loadFte = useCallback(() => {
    setLoading(true);
    let url = `/api/forecast/fte-berechnung?jahr=${year}&monat=${month}`;
    if (standortId) url += '&standort_id=' + standortId;
    api(url)
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year, month, standortId]);

  useEffect(() => { loadFte(); }, [loadFte]);

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const statusColor = (s) => s === 'red' ? '#ef4444' : s === 'orange' ? '#f59e0b' : '#22c55e';
  const statusLabel = (s) => s === 'red' ? t('timeTargets.understaffed') : s === 'orange' ? t('timeTargets.tight') : t('timeTargets.ok');
  const dayLabel = (d) => { const dt = new Date(d + 'T12:00:00'); return t('common.weekdays.' + dt.getDay()); };

  const chartData = (data?.days || []).map(d => ({
    datum: d.datum,
    label: `${dayLabel(d.datum)} ${new Date(d.datum + 'T12:00:00').getDate()}.`,
    bedarf: d.fte_bedarf,
    geplant: d.geplant_fte,
    delta: d.delta,
    status: d.status,
  }));

  return (
    <div className="space-y-4">
      {/* Month selector */}
      <div className="flex items-center gap-3">
        <button onClick={prevMonth} className="p-1 rounded hover:bg-surface-hover text-content-tertiary">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <span className="text-sm font-bold min-w-[140px] text-center">{monthNames[month - 1]} {year}</span>
        <button onClick={nextMonth} className="p-1 rounded hover:bg-surface-hover text-content-tertiary">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
        </button>
        <StandortSelect standorte={standorte} standortId={standortId} setStandortId={setStandortId} alleOption />
      </div>

      {loading && <div className="text-xs text-content-tertiary">{t('timeTargets.loadingFtePrognosis')}</div>}

      {data?.error && !data.days?.length && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-card p-3 text-sm text-amber-400">
          {data.error}
        </div>
      )}

      {data?.kpis && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: t('timeTargets.avgFteDemand'), value: data.kpis.avg_fte_bedarf.toFixed(1) },
              { label: t('timeTargets.peakFte'), value: data.kpis.peak_fte_bedarf.toFixed(1) },
              { label: t('timeTargets.avgPlanned'), value: data.kpis.avg_geplant.toFixed(1) },
              { label: t('timeTargets.understaffed'), value: `${data.kpis.unterbesetzt_tage} ${t('timeTargets.days')}` },
              { label: t('timeTargets.tight'), value: `${data.kpis.knapp_tage} ${t('timeTargets.days')}` },
            ].map((kpi, i) => (
              <KpiCard key={i} label={kpi.label} value={kpi.value} />
            ))}
          </div>

          {/* Settings info */}
          <div className="flex items-center gap-3 text-[10px] text-content-tertiary">
            <span>{t('timeTargets.pickBenchmark')}: <b className="text-[var(--text-secondary)]">{fmtMin(data.settings.pickBenchmark)}</b></span>
            <span>{t('timeTargets.packBenchmark')}: <b className="text-[var(--text-secondary)]">{fmtMin(data.settings.packBenchmark)}</b></span>
            <span>{t('timeTargets.effectivity')}: <b className="text-[var(--text-secondary)]">{(data.settings.effectivityFactor * 100).toFixed(0)}%</b></span>
            <span>{t('timeTargets.shiftDuration')}: <b className="text-[var(--text-secondary)]">{data.settings.shiftDurationMin} min</b></span>
            <span>{t('timeTargets.effMinutesPerFte')}: <b className="text-[var(--text-secondary)]">{data.settings.effectiveMinutesPerFte} min</b></span>
          </div>

          {/* Chart */}
          {chartData.length > 0 && (
            <Card title={`${t('timeTargets.fteDemandVsPlanned')} — ${monthNames[month - 1]} ${year}`}>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} barGap={2}>
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} width={30} />
                  <Tooltip
                    contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 11 }}
                    formatter={(v, name) => [typeof v === 'number' ? v.toFixed(1) : v, name === 'bedarf' ? t('timeTargets.fteDemand') : name === 'geplant' ? t('common.planned') : name]}
                    labelFormatter={(l) => l}
                  />
                  <Bar dataKey="bedarf" name={t('timeTargets.fteDemand')} radius={[3, 3, 0, 0]}>
                    {chartData.map((entry, i) => (
                      <Cell key={i} fill={statusColor(entry.status)} fillOpacity={0.7} />
                    ))}
                  </Bar>
                  <Bar dataKey="geplant" name={t('common.planned')} fill="#22d3ee" fillOpacity={0.4} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Tages-Tabelle */}
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-line">
                    <th className="text-left px-3 py-2 text-content-tertiary font-medium">{t('timeTargets.day')}</th>
                    <th className="text-right px-3 py-2 text-content-tertiary font-medium">{t('common.packages')}</th>
                    <th className="text-right px-3 py-2 text-content-tertiary font-medium">{t('common.minutes')}</th>
                    <th className="text-right px-3 py-2 text-content-tertiary font-medium">{t('timeTargets.fteDemand')}</th>
                    <th className="text-right px-3 py-2 text-content-tertiary font-medium">{t('common.planned')}</th>
                    <th className="text-right px-3 py-2 text-content-tertiary font-medium">{t('timeTargets.delta')}</th>
                    <th className="text-center px-3 py-2 text-content-tertiary font-medium">{t('common.status')}</th>
                    <th className="text-left px-3 py-2 text-content-tertiary font-medium">{t('timeTargets.topCustomers')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.days || []).map((d, i) => (
                    <tr key={d.datum} className={`border-b border-line/50 ${i % 2 === 0 ? '' : 'bg-surface/30'}`}>
                      <td className="px-3 py-1.5 font-medium text-content-secondary">
                        {dayLabel(d.datum)} {new Date(d.datum + 'T12:00:00').getDate()}.{month}.
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-content">{fmtNum(d.soll_pakete)}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-[var(--text-secondary)]">{fmtNum(Math.round(d.total_minuten))}</td>
                      <td className="px-3 py-1.5 text-right font-mono font-bold text-content">
                        {d.fte_bedarf.toFixed(1)}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-content">
                        {d.geplant_fte > 0 ? d.geplant_fte : '–'}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono font-bold text-content">
                        {d.geplant_fte > 0 ? `${d.delta >= 0 ? '+' : ''}${d.delta.toFixed(1)}` : '–'}
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ backgroundColor: statusColor(d.status) + '20', color: statusColor(d.status) }}>
                          {statusLabel(d.status)}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-[var(--text-secondary)]">
                        {(d.top_customers || []).slice(0, 3).map((c, j) => (
                          <span key={j}>
                            {j > 0 && <span className="text-content-tertiary"> · </span>}
                            <span className="text-content-secondary">{c.name.length > 15 ? c.name.slice(0, 15) + '…' : c.name}</span>
                            <span className="text-[9px] text-content-tertiary ml-0.5">
                              ({c.soll_pakete}×{c.zeit_pro_paket != null ? c.zeit_pro_paket.toFixed(1) : '?'}m)
                            </span>
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// EINSTELLUNGEN TAB — Custom Kalkulationsparameter
// ═══════════════════════════════════════════════════════════════
function PersonalplanungEinstellungen() {
  const { t } = useLanguage();
  const [cfg, setCfg] = useState({
    personalplanung_produktivitaet: 80,
    personalplanung_kalk_anwesenheitsstunden: 1738,
  });
  const [saving, setSaving] = useState({});

  useEffect(() => {
    Promise.all([
      api('/api/einstellungen/personalplanung_produktivitaet'),
      api('/api/einstellungen/personalplanung_kalk_anwesenheitsstunden'),
    ]).then(([a, b]) => {
      setCfg({
        personalplanung_produktivitaet: a?.value != null ? parseFloat(a.value) : 80,
        personalplanung_kalk_anwesenheitsstunden: b?.value != null ? parseFloat(b.value) : 1738,
      });
    }).catch(() => {});
  }, []);

  const saveKey = async (key) => {
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await fetch(`${API}/api/einstellungen/${key}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: String(cfg[key]) })
      });
    } finally {
      setSaving(s => ({ ...s, [key]: false }));
    }
  };

  const fields = [
    { key: 'personalplanung_produktivitaet', label: t('pp.produktivitaet'), unit: '%', hint: t('pp.produktivitaetHint', 'Anteil der Anwesenheitsstunden, der produktiv verrechnet wird') },
    { key: 'personalplanung_kalk_anwesenheitsstunden', label: t('pp.kalkAnwesenheitsstunden'), unit: 'h', hint: t('pp.kalkAnwesenheitsstundenHint', 'Jährliche Anwesenheitsstunden nach Abzug von Feiertagen, Urlaub, Krankenstand, Fortbildung') },
  ];

  return (
    <div className="space-y-4">
      {/* Card 1: Kalkulationsparameter */}
      <Card title={t('pp.einstellungenKalkulation')}>
        <div className="space-y-4">
          {fields.map(f => (
            <div key={f.key} className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-[var(--text-primary)]">{f.label}</div>
                <div className="text-[10px] text-[var(--text-muted)]">{f.hint}</div>
              </div>
              <div className="flex items-center gap-2">
                <input type="number" step="any"
                  value={cfg[f.key]} onChange={e => setCfg(c => ({ ...c, [f.key]: e.target.value }))}
                  className="w-24 border border-[var(--border-card)] rounded-lg px-3 py-1.5 bg-[var(--bg-card)] text-[var(--text-primary)] text-sm font-mono text-right"
                />
                <span className="text-xs text-[var(--text-tertiary)] w-4">{f.unit}</span>
                <button onClick={() => saveKey(f.key)} disabled={saving[f.key]}
                  className="px-3 py-1.5 text-xs font-medium bg-[#338edc] text-white rounded-lg hover:bg-[#2a7bc4] disabled:opacity-50">
                  {saving[f.key] ? '...' : t('common.save')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Card 2: Abzüge-Übersicht */}
      <Card title={t('pp.einstellungenAbzuege')}>
        <div className="text-xs text-[var(--text-secondary)] space-y-2">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1">
            <div className="flex justify-between"><span className="text-[var(--text-tertiary)]">{t('pp.feiertage')}</span><span className="font-mono">13</span></div>
            <div className="flex justify-between"><span className="text-[var(--text-tertiary)]">{t('pp.urlaubTage')}</span><span className="font-mono">25</span></div>
            <div className="flex justify-between"><span className="text-[var(--text-tertiary)]">{t('pp.krankenstand')}</span><span className="font-mono">5</span></div>
            <div className="flex justify-between"><span className="text-[var(--text-tertiary)]">{t('pp.fortbildung')}</span><span className="font-mono">3</span></div>
            <div className="flex justify-between"><span className="text-[var(--text-tertiary)]">{t('pp.sonderzahlungen')}</span><span className="font-mono">2 {t('pp.monateLabel')}</span></div>
          </div>
          <div className="mt-3 pt-3 border-t border-[var(--border-card)] flex items-start gap-2">
            <Info className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
            <span className="text-[10px] text-[var(--text-muted)] font-mono leading-relaxed">
              {t('pp.formelInfo', '(365 - 104 WE - 13 FT - 25 U - 5 K - 3 FB) × 8h = 1.720h → Kalk. Anwesenheitsstd. berücksichtigt Rundung/Durchschnitt')}
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
}


// ─── Period options ─────────────────────────────────────────────
const PERIODS = [
  { key: '4w', wochen: 4, monate: 1 },
  { key: '8w', wochen: 8, monate: 2 },
  { key: 'quartal', wochen: 13, monate: 3 },
];

export default function Personalplanung() {
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useTabParam('uebersicht');
  const { standorte } = useStandort();
  const [standortId, setStandortIdRaw] = useState(null); // default: alle Standorte
  const setStandortId = (v) => setStandortIdRaw(v === 'alle' || !v ? null : Number(v));
  const [period, setPeriod] = useState('8w');
  const [weekData, setWeekData] = useState(null);
  const [maData, setMaData] = useState(null);
  const [costData, setCostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maExpanded, setMaExpanded] = useState(false);

  const periodCfg = PERIODS.find(p => p.key === period) || PERIODS[1];

  const load = useCallback(() => {
    setLoading(true);
    const qs = standortId ? `&standort_id=${standortId}` : '';
    const today = new Date().toLocaleDateString('sv-SE');
    Promise.all([
      api(`/api/personalplanung?start=${today}&wochen=${periodCfg.wochen}${qs}`),
      api(`/api/personalplanung/mitarbeiter?start=${today}&wochen=${periodCfg.wochen}${qs}`),
      api(`/api/personalplanung/kosten?start=${today}&monate=${periodCfg.monate}${qs}`),
    ]).then(([w, m, c]) => {
      setWeekData(w);
      setMaData(m);
      setCostData(c);
    }).finally(() => setLoading(false));
  }, [standortId, periodCfg]);

  useEffect(() => { load(); }, [load]);

  // ─── KPI summary ──────────────────────────────────────────────
  const kpi = useMemo(() => {
    if (!weekData?.weeks?.length) return null;
    // Find first week with forecast data (not keine_daten)
    const nextWeek = weekData.weeks.find(w => w.status.gesamt !== 'keine_daten') || weekData.weeks[0];
    const delta = nextWeek.verfuegbar.gesamt_fte - nextWeek.bedarf.gesamt_fte;
    const monthCost = costData?.months?.[0]?.kosten?.personal || 0;
    return {
      fte_bedarf: nextWeek.bedarf.gesamt_fte,
      fte_verfuegbar: nextWeek.verfuegbar.gesamt_fte,
      delta: Math.round(delta * 100) / 100,
      kosten_monat: monthCost,
      kw: nextWeek.kw,
    };
  }, [weekData, costData]);

  // ─── Chart data ───────────────────────────────────────────────
  const chartData = useMemo(() => {
    if (!weekData?.weeks) return [];
    return weekData.weeks.map(w => ({
      name: `${t('personalplanung.kw')} ${w.kw}`,
      pick: w.bedarf.pick_fte,
      pack: w.bedarf.pack_fte,
      verfuegbar: w.verfuegbar.gesamt_fte,
    }));
  }, [weekData, t]);

  // ─── Date formatter ───────────────────────────────────────────
  const fmtWeek = (w) => {
    const von = new Date(w.von + 'T12:00:00');
    const bis = new Date(w.bis + 'T12:00:00');
    const fmtD = (d) => `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')}.`;
    return `${t('personalplanung.kw')} ${w.kw} (${fmtD(von)}–${fmtD(bis)})`;
  };

  const fmtMonth = (m) => {
    const d = new Date(m.jahr, m.monat - 1, 1);
    return d.toLocaleDateString(locale === 'de' ? 'de-AT' : 'en-US', { month: 'long', year: 'numeric' });
  };

  const fmtEur = (v) => {
    if (v >= 1000) return `€${Math.round(v).toLocaleString(locale === 'de' ? 'de-AT' : 'en-US')}`;
    return `€${v}`;
  };

  // ─── Status badge ─────────────────────────────────────────────
  const StatusBadge = ({ status }) => {
    const map = {
      ok: { label: 'OK', cls: 'bg-emerald-500/10 text-emerald-600' },
      ueberdeckung: { label: t('personalplanung.ueberdeckung'), cls: 'bg-amber-500/10 text-amber-600' },
      unterdeckung: { label: t('personalplanung.unterdeckung'), cls: 'bg-red-500/10 text-red-600' },
      keine_daten: { label: '–', cls: 'bg-gray-100 text-[var(--text-tertiary,#6B7280)]' },
    };
    const s = map[status] || map.keine_daten;
    return <span className={`inline-block px-2 py-0.5 rounded-full text-[12px] font-medium ${s.cls}`}>{s.label}</span>;
  };

  // ─── Period toggle ────────────────────────────────────────────
  const PeriodToggle = () => (
    <div className="flex bg-surface-inset border border-line rounded-lg overflow-hidden">
      {PERIODS.map(p => (
        <button
          key={p.key}
          onClick={() => setPeriod(p.key)}
          className={`px-3 py-1.5 text-xs font-medium transition-all ${
            period === p.key
              ? 'bg-white text-content shadow-sm'
              : 'text-content-secondary hover:text-content'
          }`}
        >
          {t(`personalplanung.period${p.key}`)}
        </button>
      ))}
    </div>
  );

  if (loading && !weekData) {
    return (
      <div className="p-6">
        <PageHeader title={t('personalplanung.title')} />
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-surface-card border border-line-card rounded-card animate-pulse" />)}
        </div>
        <div className="h-64 bg-surface-card border border-line-card rounded-card animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <PageHeader
        title={t('personalplanung.title')}
        actions={activeTab === 'uebersicht' ? (
          <div className="flex items-center gap-3">
            <StandortSelect standorte={standorte} standortId={standortId} setStandortId={setStandortId} alleOption />
            <PeriodToggle />
          </div>
        ) : null}
      />

      {/* Tab Bar */}
      <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] gap-[2px] border border-[var(--border-card,#E5E7EB)]">
        {[
          { key: 'uebersicht', label: t('personalplanung.tabs.wochenUebersicht') },
          { key: 'monatsplanung', label: t('personalplanung.tabs.monatsplanung') },
          { key: 'stundensatz', label: t('personalplanung.tabs.stundensatz') },
          { key: 'ftePrognose', label: t('personalplanung.tabs.ftePrognose') },
          { key: 'einstellungen', label: t('modulSettings.title', 'Einstellungen') },
        ].map(tb => (
          <button
            key={tb.key}
            onClick={() => setActiveTab(tb.key)}
            className={`px-4 py-1.5 text-xs font-medium rounded-[8px] transition-all ${
              activeTab === tb.key
                ? 'bg-white text-[var(--text-primary,#1A1A2E)] shadow-sm font-semibold'
                : 'text-[var(--text-secondary,#4B5563)] hover:text-[var(--text-primary,#1A1A2E)]'
            }`}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {/* Monatsplanung Tab */}
      {activeTab === 'monatsplanung' && <MonatsplanungTab />}

      {/* Stundensatz Tab */}
      {activeTab === 'stundensatz' && <StundensatzTab />}

      {/* FTE-Prognose Tab */}
      {activeTab === 'ftePrognose' && <FtePrognoseTab />}

      {/* Einstellungen Tab */}
      {activeTab === 'einstellungen' && <PersonalplanungEinstellungen />}

      {/* Wochen-Übersicht Tab */}
      {activeTab === 'uebersicht' && <>

      {/* KPI Cards */}
      {kpi && (
        <div className="grid grid-cols-4 gap-4">
          <KpiCard
            label={`${t('personalplanung.fteBedarf')} (${t('personalplanung.kw')} ${kpi.kw})`}
            value={kpi.fte_bedarf}
            icon={Users}
            color="bg-blue-500/10 text-blue-500"
          />
          <KpiCard
            label={`${t('personalplanung.fteVerfuegbar')}`}
            value={kpi.fte_verfuegbar}
            icon={Users}
            color="bg-indigo-500/10 text-indigo-500"
          />
          <KpiCard
            label="Delta"
            value={kpi.delta > 0 ? `+${kpi.delta}` : kpi.delta}
            trend={kpi.delta >= 0 ? `+${kpi.delta} FTE` : `${kpi.delta} FTE`}
            trendUp={kpi.delta >= 0}
            icon={kpi.delta >= 0 ? TrendingUp : TrendingDown}
            color={kpi.delta >= 0 ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}
          />
          <KpiCard
            label={t('personalplanung.personalkosten')}
            value={fmtEur(kpi.kosten_monat)}
            icon={Euro}
            color="bg-cyan-500/10 text-cyan-500"
          />
        </div>
      )}

      {/* Weekly Overview Table */}
      {weekData?.weeks && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-line">
                  {['', t('personalplanung.kw'), 'SOLL PKT', 'FTE PICK', 'FTE PACK', 'FTE GESAMT', t('personalplanung.fteVerfuegbar').toUpperCase(), t('personalplanung.arbeitstage').toUpperCase(), 'DELTA', 'STATUS'].map((h, i) => (
                    <th key={i} className="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary px-3 py-2.5">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weekData.weeks.map((w, idx) => {
                  const delta = w.verfuegbar.gesamt_fte - w.bedarf.gesamt_fte;
                  return (
                    <tr key={idx} className="border-b border-line/50 hover:bg-surface-inset/50 transition-colors">
                      <td className="px-3 py-2.5">
                        <div className={`w-2 h-2 rounded-full ${
                          w.status.gesamt === 'ok' ? 'bg-emerald-500' :
                          w.status.gesamt === 'unterdeckung' ? 'bg-red-500' :
                          w.status.gesamt === 'ueberdeckung' ? 'bg-amber-500' : 'bg-gray-300'
                        }`} />
                      </td>
                      <td className="px-3 py-2.5 text-sm text-content font-medium whitespace-nowrap">{fmtWeek(w)}</td>
                      <td className="px-3 py-2.5 text-sm text-content tabular-nums">{w.bedarf.pakete || '–'}</td>
                      <td className="px-3 py-2.5 text-sm text-content tabular-nums">{w.bedarf.pick_fte || '–'}</td>
                      <td className="px-3 py-2.5 text-sm text-content tabular-nums">{w.bedarf.pack_fte || '–'}</td>
                      <td className="px-3 py-2.5 text-sm text-content font-semibold tabular-nums">{w.bedarf.gesamt_fte || '–'}</td>
                      <td className="px-3 py-2.5 text-sm text-content tabular-nums">{w.verfuegbar.gesamt_fte}</td>
                      <td className="px-3 py-2.5 text-sm text-content tabular-nums">{w.arbeitstage}</td>
                      <td className="px-3 py-2.5 text-sm tabular-nums">
                        {w.status.gesamt !== 'keine_daten' ? (
                          <span className={delta >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                            {delta > 0 ? '+' : ''}{Math.round(delta * 100) / 100}
                          </span>
                        ) : '–'}
                      </td>
                      <td className="px-3 py-2.5"><StatusBadge status={w.status.gesamt} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Trend Chart */}
      {chartData.length > 0 && (
        <Card title={t('personalplanung.trendChart')}>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-card, #E5E7EB)" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }} label={{ value: 'FTE', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: 'var(--text-tertiary)' } }} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'var(--surface-card)', border: '1px solid var(--line-card)', borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ fontWeight: 600 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="pick" stackId="fte" name="FTE Pick" fill="#338edc" radius={[0, 0, 0, 0]} />
                <Bar dataKey="pack" stackId="fte" name="FTE Pack" fill="#22D3EE" radius={[4, 4, 0, 0]} />
                <Line
                  type="monotone"
                  dataKey="verfuegbar"
                  name={t('personalplanung.fteVerfuegbar')}
                  stroke="#6366F1"
                  strokeWidth={2}
                  strokeDasharray="6 3"
                  dot={{ r: 3, fill: '#6366F1' }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Staff Availability (collapsible) */}
      <Card>
        <button
          onClick={() => { setMaExpanded(!maExpanded); }}
          className="w-full flex items-center justify-between text-left"
        >
          <h3 className="text-sm font-semibold text-content">{t('personalplanung.expandStaff')}</h3>
          {maExpanded ? <ChevronUp className="w-4 h-4 text-content-secondary" /> : <ChevronDown className="w-4 h-4 text-content-secondary" />}
        </button>

        {maExpanded && maData?.mitarbeiter && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-line">
                  <th className="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary px-3 py-2 sticky left-0 bg-surface-card z-10">MA</th>
                  <th className="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary px-3 py-2">{t('personalplanung.lagerRolle').toUpperCase()}</th>
                  {maData.weeks.map((w, i) => (
                    <th key={i} className="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary px-3 py-2 text-center">
                      {t('personalplanung.kw')} {w.kw}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {maData.mitarbeiter.map(ma => {
                  const rolleLabel = ma.lager_rolle === 'picker' ? t('personalplanung.picker')
                    : ma.lager_rolle === 'packer' ? t('personalplanung.packer')
                    : t('personalplanung.pickPack');
                  return (
                    <tr key={ma.id} className="border-b border-line/30 hover:bg-surface-inset/50 transition-colors">
                      <td className="px-3 py-2 text-sm text-content font-medium whitespace-nowrap sticky left-0 bg-surface-card z-10">
                        <MitarbeiterListItem
                          mitarbeiter={ma}
                          size="sm"
                          onClick={() => navigate(`/mitarbeiter/${ma.id}`)}
                        />
                      </td>
                      <td className="px-3 py-2 text-xs text-content-secondary whitespace-nowrap">{rolleLabel}</td>
                      {ma.wochen.map((w, wi) => (
                        <td key={wi} className="px-3 py-2 text-center">
                          {w.abwesende_tage > 0 && w.verfuegbare_tage === 0 ? (
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500/10" title={t('personalplanung.sick')}>
                              <Thermometer className="w-3.5 h-3.5 text-red-500" />
                            </span>
                          ) : w.abwesende_tage > 0 ? (
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500/10" title={`${w.abwesende_tage}d ${t('personalplanung.vacation')}`}>
                              <Palmtree className="w-3.5 h-3.5 text-amber-500" />
                            </span>
                          ) : w.verfuegbare_tage === 0 ? (
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100" title={t('personalplanung.holiday')}>
                              <CalendarOff className="w-3.5 h-3.5 text-[var(--text-tertiary,#6B7280)]" />
                            </span>
                          ) : (
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/10" title={`${w.verfuegbare_tage}d / ${w.stunden}h`}>
                              <Check className="w-3.5 h-3.5 text-emerald-500" />
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Cost Preview */}
      {costData?.months && (
        <Card title={t('personalplanung.kostenprognose')}>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-line">
                  {['MONAT', 'FTE Ø/TAG', t('personalplanung.arbeitstage').toUpperCase(), 'STUNDEN', t('personalplanung.personalkosten').toUpperCase(), t('personalplanung.auslastung').toUpperCase(), t('personalplanung.proPaket').toUpperCase()].map((h, i) => (
                    <th key={i} className="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary px-3 py-2.5">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {costData.months.map((m, idx) => (
                  <tr key={idx} className="border-b border-line/50 hover:bg-surface-inset/50 transition-colors">
                    <td className="px-3 py-2.5 text-sm text-content font-medium">{fmtMonth(m)}</td>
                    <td className="px-3 py-2.5 text-sm text-content tabular-nums">{m.verfuegbar.fte_durchschnitt}</td>
                    <td className="px-3 py-2.5 text-sm text-content tabular-nums">{m.arbeitstage}</td>
                    <td className="px-3 py-2.5 text-sm text-content tabular-nums">{m.verfuegbar.stunden.toLocaleString(locale === 'de' ? 'de-AT' : 'en-US')}h</td>
                    <td className="px-3 py-2.5 text-sm text-content font-semibold tabular-nums">{fmtEur(m.kosten.personal)}</td>
                    <td className="px-3 py-2.5 text-sm tabular-nums">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[12px] font-medium ${
                        m.auslastung_pct >= 80 ? 'bg-emerald-500/10 text-emerald-600' :
                        m.auslastung_pct >= 50 ? 'bg-amber-500/10 text-amber-600' :
                        'bg-gray-100 text-[var(--text-secondary,#4B5563)]'
                      }`}>
                        {m.auslastung_pct}%
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-sm text-content tabular-nums">{m.kosten.pro_paket > 0 ? `€${m.kosten.pro_paket.toFixed(2)}` : '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {weekData?.config && (
            <p className="text-[11px] text-content-tertiary mt-3">
              {t('personalplanung.costBasis', {
                rate: weekData.config.stunden_pro_tag ? Math.round(costData?.months?.[0]?.kosten?.pro_stunde_effektiv || 27) : 27,
                hours: weekData.config.stunden_pro_tag,
                productivity: weekData.config.produktivitaet_pct,
              })}
            </p>
          )}
        </Card>
      )}

      </>}
    </div>
  );
}
